import React from "react";
import set from "@rill/set";
import submit from "submit-form";
import SettingsNav from "../../../parts/settings-nav";
import moment from "moment";
import momentTimeZone from "moment-timezone";
import { checkOrganizationSectionPermission } from "../../../middleware/ensure-coach-permissions";
import Select from "../../../parts/inputs/select";
import { SettingsBackHandler } from "../../coaching-portal/settings/components/SettingsBackHandler";
import { CoachBillingReport } from "./coach-billing/CoachBillingReportComponent";
import { OrganizationBillingReportComponent } from "./coach-billing/OrganizationBillingReportComponent";
import { SummaryOrganizationBillingModalComponent } from "./coach-billing/SummaryOrganizationBillingModalComponent";

const enumerateMonths = (startDate, momentTZ) => {
  const dateEnd = momentTZ().startOf("month");
  let dateStart = momentTZ(startDate).startOf("month");
  let months = [];

  while (!dateStart.isAfter(dateEnd)) {
    months.push({
      label: dateStart.format("MMMM YYYY"),
      month: dateStart.month() + 1,
      year: dateStart.year(),
    });
    dateStart = dateStart.add(1, "month");
  }

  return months;
};

class CoachBillingDetails extends React.Component {
  constructor() {
    super();
    this.state = {
      editCreditInfo: false,
      selectedMonth: "",
    };
    this.handleProgramChange = this.handleProgramChange.bind(this);
  }

  handleProgramChange(value) {
    this.setState({ selectedMonth: value });
    submit("/actions/refresh", {
      method: "GET",
      body: { date: value },
    });
  }

  render() {
    const { locals } = this.context;
    const { organizationBilling, coachingBilling, activeUser } = locals;
    const { webAppTheme = "dark" } = activeUser;
    const { months, isCurrentMonth } = this.props;
    const defaultMonth = months[months.length - 1];
    const report =
      organizationBilling?.report ?? coachingBilling?.report ?? null;
    const plan = organizationBilling?.plan ?? coachingBilling?.plan ?? null;
    const receipts = locals?.coachingReceipts?.receipts || undefined;
    const downloadReceiptUrl =
      (receipts && (receipts[0]?.receipt_url || receipts[1]?.receipt_url)) ||
      null;

    return (
      <main className="settings">
        <SettingsNav classNames="mobile-hidden" />

        <section className="coach-billing">
          <h3 className="force-mobile-hidden">Coach Billing</h3>
          <SettingsBackHandler title={"Coach Billing"} />
          <p className="s2 mb-6 -mt-2">
            View details and receipts of billings for usage of your PULL
            (coaching) account.
          </p>
          <form id="coach-billing-form" action="#_" method="GET" data-noreset>
            <header>
              <hgroup role="input-group">
                <Select
                  name="date"
                  options={[...months].reverse().map((m) => ({
                    text: m.label,
                    value: `${m.month}-${m.year}`,
                  }))}
                  onChange={this.handleProgramChange}
                  title={"Billing Month"}
                  value={
                    this.state.selectedMonth ||
                    `${defaultMonth.month}-${defaultMonth.year}`
                  }
                />
              </hgroup>
              {/* Condition for stealth deploy lol */}
              {downloadReceiptUrl && (
                <button
                  data-button-type="primary"
                  onClick={(e) => {
                    e.preventDefault();
                    window.open(downloadReceiptUrl, "_blank");
                  }}
                >
                  Download Receipt
                </button>
              )}
            </header>

            {report ? (
              organizationBilling ? (
                <OrganizationBillingReportComponent
                  report={report}
                  isCurrentMonth={isCurrentMonth}
                  webAppTheme={webAppTheme}
                />
              ) : (
                <CoachBillingReport
                  report={report}
                  isCurrentMonth={isCurrentMonth}
                  plan={plan}
                />
              )
            ) : (
              <p className="s1">No report for this month yet</p>
            )}
          </form>
          <form
            action="/dashboard/settings/coach-billing"
            method="POST"
            className="form-cancel-subscription"
            id="cancel-subscription-form"
          >
            <button
              data-button-type="secondary"
              data-action="cancel"
              type="submit"
              form="cancel-subscription-form"
            >
              Cancel Subscription
            </button>
          </form>
        </section>
      </main>
    );
  }
}

const CoachBilling = (app) => {
  app
    .get(
      "/coach-billing",
      set("heading", "Coach Billing"),
      (ctx, next) =>
        checkOrganizationSectionPermission(
          ctx,
          next,
          "settings",
          "/coaching-portal/settings",
          ["Owner"]
        ),
      async ({ res, req, locals, momentTZ, load }) => {
        const { query } = req;
        const modal = query.modal || {};
        const { activeUser, activeUserToRequest } = locals;
        const { organizationId = null } = activeUser;
        const { _id: ownerId } = activeUserToRequest;

        const months = enumerateMonths(
          activeUserToRequest.coachingBilling &&
            activeUserToRequest.coachingBilling.startedAt
            ? activeUserToRequest.coachingBilling.startedAt
            : momentTZ(),
          momentTZ
        );

        const today = momentTZ();
        const defaultDate = `${today.month() + 1}-${today.year()}`;
        const date = query.date
          ? query.date.split("-")
          : defaultDate.split("-");
        const [month, year] = date;

        organizationId
          ? await load(
              "organizationBilling",
              month,
              year,
              ownerId,
              organizationId
            )
          : await load("coachingBilling", month, year);

        const monthString = moment()
          .month(month - 1)
          .format("MMMM");

        const startDateTZ = momentTimeZone()
          .month(monthString)
          .year(year)
          .startOf("month")
          .tz("America/Halifax")
          .format();
        const endDateTZ = momentTimeZone()
          .month(monthString)
          .year(year)
          .endOf("month")
          .tz("America/Halifax")
          .format();

        const startDateUnix = moment(startDateTZ).unix();
        const endDateUnix = moment(endDateTZ).unix();

        await load("coachingReceipts", startDateUnix, endDateUnix);

        const isCurrentMonth = Number(today.month() + 1) === Number(month);

        //load modal
        locals.modal = {
          "summary-organization-billing": (
            <SummaryOrganizationBillingModalComponent locals={locals} />
          ),
        }[modal.type];

        res.body = (
          <CoachBillingDetails
            months={months}
            isCurrentMonth={isCurrentMonth}
          />
        );
      }
    )
    .post(
      "/coach-billing",
      (ctx, next) =>
        checkOrganizationSectionPermission(
          ctx,
          next,
          "settings",
          "/coaching-portal/settings",
          ["Owner"]
        ),
      async ({ res, req, api, flash }) => {
        if (
          process.browser &&
          !confirm(
            "Are you sure you want to cancel your coaching subscription? If yes, you and your clients will lose access to your accounts immediately and you will no longer be billed"
          )
        ) {
          return res.backDontScroll();
        }

        await api(`stripe-remove-coach-subscription`, {
          method: "PUT",
          body: {},
        });
        flash("success", "Successfully cancelled subscription");

        res.redirect(`/logout`);
      }
    );
};

export default CoachBilling;
